import { List, Map, Set } from 'immutable';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isEqual from 'lodash/isEqual';
import isUndefined from 'lodash/isUndefined';
import map from 'lodash/map';

import {
  AllTypes,
  CollectionActions as OwnActions,
  CollectionKeys,
  CollectionSorts,
  FilterTypes,
  PandoraTypeToFilterTypeMap,
  PodcastTypes,
  RECENTLY_PLAYED_PODCASTS_MAX_LIST_SIZE,
} from './constants';
import * as D from './deps';
import {
  getFilterFromTypePrefixes,
  getFilterFromView,
  hasCollectionUpdateSucceeded,
  isPlaylistAction,
} from './utils';

const log = D.Logger.child({ component: 'domain/collection/reducer' });

const MUTABLE_FILTERS = Object.freeze({
  [FilterTypes.ALL]: null,
  [FilterTypes.ALBUMS]: D.PandoraTypes.ALBUM,
  [FilterTypes.SONGS]: D.PandoraTypes.SONG,
  [FilterTypes.PLAYLISTS]: D.PandoraTypes.PLAYLIST,
  [CollectionSorts.RECENT_MODIFIED_PLAYLISTS]: D.PandoraTypes.PLAYLIST,
});

export const SORTED_FILTERS_INITIAL_STATE = Map({
  [FilterTypes.ALL]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.FETCHED_OFFSETS]: Set(),
    [CollectionKeys.FETCHING_OFFSETS]: Set(),
    [CollectionKeys.CURRENT_OFFSET]: null,
    [CollectionKeys.CURRENT_VIEWED_OFFSET]: null,
    [CollectionKeys.PREVIOUS_OFFSET]: null,
    [CollectionKeys.SHOULD_RELOAD]: true,
    [CollectionKeys.SORT]: D.MyMusicSorts.RECENT,
    [CollectionKeys.TOTAL_COUNT]: null,
    [CollectionKeys.PREVIOUS_TOTAL_COUNT]: null,
  }),
  [FilterTypes.ARTISTS]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.FETCHED_OFFSETS]: Set(),
    [CollectionKeys.FETCHING_OFFSETS]: Set(),
    [CollectionKeys.CURRENT_OFFSET]: null,
    [CollectionKeys.CURRENT_VIEWED_OFFSET]: null,
    [CollectionKeys.PREVIOUS_OFFSET]: null,
    [CollectionKeys.SHOULD_RELOAD]: true,
    [CollectionKeys.SORT]: D.MyMusicSorts.A_TO_Z,
    [CollectionKeys.TOTAL_COUNT]: null,
    [CollectionKeys.PREVIOUS_TOTAL_COUNT]: null,
  }),
  [FilterTypes.ALBUMS]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.FETCHED_OFFSETS]: Set(),
    [CollectionKeys.FETCHING_OFFSETS]: Set(),
    [CollectionKeys.CURRENT_OFFSET]: null,
    [CollectionKeys.CURRENT_VIEWED_OFFSET]: null,
    [CollectionKeys.PREVIOUS_OFFSET]: null,
    [CollectionKeys.SHOULD_RELOAD]: true,
    [CollectionKeys.SORT]: D.MyMusicSorts.RECENT,
    [CollectionKeys.TOTAL_COUNT]: null,
    [CollectionKeys.PREVIOUS_TOTAL_COUNT]: null,
  }),
  [FilterTypes.SONGS]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.FETCHED_OFFSETS]: Set(),
    [CollectionKeys.FETCHING_OFFSETS]: Set(),
    [CollectionKeys.CURRENT_OFFSET]: null,
    [CollectionKeys.CURRENT_VIEWED_OFFSET]: null,
    [CollectionKeys.PREVIOUS_OFFSET]: null,
    [CollectionKeys.SHOULD_RELOAD]: true,
    [CollectionKeys.SORT]: D.MyMusicSorts.RECENT,
    [CollectionKeys.TOTAL_COUNT]: null,
    [CollectionKeys.PREVIOUS_TOTAL_COUNT]: null,
  }),
  [FilterTypes.PLAYLISTS]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.FETCHED_OFFSETS]: Set(),
    [CollectionKeys.FETCHING_OFFSETS]: Set(),
    [CollectionKeys.CURRENT_OFFSET]: null,
    [CollectionKeys.CURRENT_VIEWED_OFFSET]: null,
    [CollectionKeys.PREVIOUS_OFFSET]: null,
    [CollectionKeys.SHOULD_RELOAD]: true,
    [CollectionKeys.SORT]: D.MyMusicSorts.RECENT,
    [CollectionKeys.TOTAL_COUNT]: null,
    [CollectionKeys.PREVIOUS_TOTAL_COUNT]: null,
  }),
  [CollectionSorts.RECENT_MODIFIED_PLAYLISTS]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.FETCHED_OFFSETS]: Set(),
    [CollectionKeys.FETCHING_OFFSETS]: Set(),
    [CollectionKeys.CURRENT_OFFSET]: null,
    [CollectionKeys.CURRENT_VIEWED_OFFSET]: null,
    [CollectionKeys.PREVIOUS_OFFSET]: null,
    [CollectionKeys.SHOULD_RELOAD]: true,
    [CollectionKeys.SORT]: CollectionSorts.RECENT_MODIFIED,
    [CollectionKeys.TOTAL_COUNT]: null,
    [CollectionKeys.PREVIOUS_TOTAL_COUNT]: null,
  }),
  [FilterTypes.PODCASTS]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.FETCHED_OFFSETS]: Set(),
    [CollectionKeys.FETCHING_OFFSETS]: Set(),
    [CollectionKeys.CURRENT_OFFSET]: null,
    [CollectionKeys.CURRENT_VIEWED_OFFSET]: null,
    [CollectionKeys.PREVIOUS_OFFSET]: null,
    [CollectionKeys.SHOULD_RELOAD]: true,
    [CollectionKeys.SORT]: D.MyPodcastSorts.RECENTLY_COLLECTED,
    [CollectionKeys.TOTAL_COUNT]: null,
    [CollectionKeys.PREVIOUS_TOTAL_COUNT]: null,
  }),
  [FilterTypes.EPISODES]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.FETCHED_OFFSETS]: Set(),
    [CollectionKeys.FETCHING_OFFSETS]: Set(),
    [CollectionKeys.CURRENT_OFFSET]: null,
    [CollectionKeys.CURRENT_VIEWED_OFFSET]: null,
    [CollectionKeys.PREVIOUS_OFFSET]: null,
    [CollectionKeys.SHOULD_RELOAD]: true,
    [CollectionKeys.SORT]: D.MyPodcastSorts.RECENTLY_COLLECTED,
    [CollectionKeys.TOTAL_COUNT]: null,
    [CollectionKeys.PREVIOUS_TOTAL_COUNT]: null,
  }),
  // Note: Stations are managed by MyStations
});

export const COLLECTION_INITIAL_STATE = Map({
  [CollectionKeys.COLLECTION]: Map({
    [CollectionKeys.ITEMS]: List(),
    [CollectionKeys.CURSOR]: null,
  }),
  [CollectionKeys.PREVIOUS_VERSION]: null,
  [CollectionKeys.VERSION]: null,
  [CollectionKeys.FETCHED_DATA_SUCCESS_KEY]: false,
  [CollectionKeys.SORTED_FILTERS]: SORTED_FILTERS_INITIAL_STATE,
  [CollectionKeys.CURRENT_FILTER]: FilterTypes.ALL,
  // @ts-expect-error TS(7009) FIXME: 'new' expression, whose target lacks a construct s... Remove this comment to see the full error message
  [CollectionKeys.ARTISTS_LIST]: new List(),
  // @ts-expect-error TS(7009) FIXME: 'new' expression, whose target lacks a construct s... Remove this comment to see the full error message
  [CollectionKeys.ARTISTS_ITEMS_ID_LIST]: new List(),
  [CollectionKeys.SHOULD_RELOAD_ARTIST_VIEW]: true,
  [CollectionKeys.IS_CURRENTLY_SYNCING]: false,
});

// This transform just persists the SORTs for each source
// @ts-expect-error TS(2345) FIXME: Argument of type '{ reducer: string; keys: "SORTED... Remove this comment to see the full error message
export const CollectionFilterTransform = D.createFilter({
  reducer: 'collection',
  keys: [CollectionKeys.SORTED_FILTERS],
  defaults: COLLECTION_INITIAL_STATE,
  mergeDeep: true,
  inbound(state: $TSFixMe) {
    if (!state) return state;
    const outState = COLLECTION_INITIAL_STATE;
    const sources = SORTED_FILTERS_INITIAL_STATE.keySeq();
    return outState.withMutations(substate => {
      sources.forEach(filterType => {
        substate.setIn(
          [CollectionKeys.SORTED_FILTERS, filterType, CollectionKeys.SORT],
          state.getIn([CollectionKeys.SORTED_FILTERS, filterType, CollectionKeys.SORT]),
        );
      });
      return substate;
    });
  },
});

export const RECENTLY_PLAYED_ITEMS_INITIAL_STATE = Map({
  [CollectionKeys.RECENTLY_PLAYED_SPS_ITEMS]: Map(),
  [CollectionKeys.RECENTLY_PLAYED_PODCASTS]: Map(),
  [CollectionKeys.HAS_MET_RECENTLY_PLAYED_THRESHOLD]: false,
});

// COLLECTION REDUCER
export const CollectionReducer = D.createReducer(COLLECTION_INITIAL_STATE, {
  [OwnActions.SET_FETCHING_OFFSETS]: setFetchingOffsets,
  [OwnActions.RESET_FILTER_OFFSETS]: resetFilterOffsets,
  [OwnActions.SET_FILTER_CURRENT_OFFSET]: setFilterCurrentViewedOffset,
  [D.ApiActions.COLLECTIONS_GET_ITEMS_SUCCESS]: onGetItems,
  [D.ApiActions.COLLECTIONS_ADD_ITEM_SUCCESS]: onUpdateItems,
  [D.ApiActions.COLLECTIONS_REMOVE_ITEM_SUCCESS]: onUpdateItems,
  [D.ApiActions.COLLECTIONS_GET_ITEMS_BY_ARTIST_SUCCESS]: onGetItemsByArtistSuccess,
  [D.ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_ATTEMPT]: resetFetchedDataSuccess,
  [D.ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_V6_ATTEMPT]: resetFetchedDataSuccess,
  [D.ApiActions.COLLECTIONS_GET_SORTED_PLAYLISTS_ATTEMPT]: resetFetchedDataSuccess,
  // @ts-expect-error TS(2551) FIXME: Property 'COLLECTIONS_GET_SORTED_PODCASTS_ATTEMPT'... Remove this comment to see the full error message
  [D.ApiActions.COLLECTIONS_GET_SORTED_PODCASTS_ATTEMPT]: resetFetchedDataSuccess,
  [D.ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_SUCCESS]: onGetSortedItemsSuccess,
  [D.ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_V6_SUCCESS]: onGetSortedItemsSuccess,
  [D.ApiActions.COLLECTIONS_GET_SORTED_PLAYLISTS_SUCCESS]: onGetSortedItemsSuccess,
  // @ts-expect-error TS(2551) FIXME: Property 'COLLECTIONS_GET_SORTED_PODCASTS_SUCCESS'... Remove this comment to see the full error message
  [D.ApiActions.COLLECTIONS_GET_SORTED_PODCASTS_SUCCESS]: onGetSortedItemsSuccess,
  [D.ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_FAIL]: onGetSortedItemsFail,
  [D.ApiActions.COLLECTIONS_GET_SORTED_BY_TYPES_V6_FAIL]: onGetSortedItemsFail,
  [D.ApiActions.COLLECTIONS_GET_SORTED_PLAYLISTS_FAIL]: onGetSortedItemsFail,
  // @ts-expect-error TS(2551) FIXME: Property 'COLLECTIONS_GET_SORTED_PODCASTS_FAIL' do... Remove this comment to see the full error message
  [D.ApiActions.COLLECTIONS_GET_SORTED_PODCASTS_FAIL]: onGetSortedItemsFail,
  // FIXME: this would be much better managed with the LOCATION_CHANGE action, but
  // due to the fact that the collection domain isn't loaded fully when the page first
  // loads, we miss the first LOCATION_CHANGE. This class of problem likely needs
  // a more elegant solution.
  [D.CollectionActions.SET_COLLECTION_CURRENT_FILTER]: setCurrentNavFilter,
  [D.CollectionActions.SET_COLLECTION_CURRENT_FILTER_SORT]: setCurrentFilterSort,
  [D.ApiActions.PLAYLISTS_CREATE_SUCCESS]: onCreatePlaylist,
});

export const RecentlyPlayedItemsReducer = D.createReducer(RECENTLY_PLAYED_ITEMS_INITIAL_STATE, {
  [OwnActions.ADD_RECENTLY_PLAYED_PODCASTS]: addRecentlyPlayedPodcasts,
  [OwnActions.SET_RECENTLY_PLAYED_SPS_ITEMS]: setRecentlyPlayedSPSItems,
  [OwnActions.SET_HAS_MET_RECENTLY_PLAYED_THRESHOLD]: setHasMetRecentlyPlayedThreshold,
});

function updateCollection(
  state: $TSFixMe,
  collectionItemsState: $TSFixMe,
  version: $TSFixMe,
  cursor: $TSFixMe,
): $TSFixMe {
  const previousVersion = state.get(CollectionKeys.VERSION);
  let collectionState = state.get(CollectionKeys.COLLECTION);
  collectionState = collectionState.merge({
    [CollectionKeys.ITEMS]: collectionItemsState,
    [CollectionKeys.CURSOR]: cursor,
  });
  return state.merge({
    [CollectionKeys.COLLECTION]: collectionState,
    [CollectionKeys.PREVIOUS_VERSION]: previousVersion ? previousVersion : version,
    [CollectionKeys.VERSION]: version,
    [CollectionKeys.IS_CURRENTLY_SYNCING]: cursor ? true : false,
  });
}

function generateAlbumsRemovedIds(removed: $TSFixMe, groupedTracksByAlbum: $TSFixMe): $TSFixMe {
  const removedIds: $TSFixMe = [];
  const albumIdsHandled: $TSFixMe = [];

  forEach(removed, item => {
    // In all cases we want to make sure the item is removed at minimum
    if (D.PandoraTypes.SONG === item.pandoratype) {
      if (includes(albumIdsHandled, item.albumPandoraId)) {
        return;
      }
      albumIdsHandled.push(item.albumPandoraId);
      const albumTracks = groupedTracksByAlbum[item.albumPandoraId] || [];
      if (!albumTracks.length) {
        removedIds.push(item.pandoraId);
      }
    } else {
      removedIds.push(item.pandoraId);
    }
  });

  return removedIds;
}

function generateSongsRemovedIds(removed: $TSFixMe): $TSFixMe {
  const removedIds: $TSFixMe = [];

  forEach(removed, item => {
    if (D.PandoraTypes.SONG === item.pandoraType) {
      removedIds.push(item.pandoraId);
    }
  });

  return removedIds;
}

function updateFilters(state: $TSFixMe, _added = [], removed = []): $TSFixMe {
  // Due to random access lazy loading of the filtered views as well as different sorts, we can't
  // reliably add items from a collection-manipulating api diff, so for now we're just removing
  // items directly and ignoring adds and counting on a worker elsewhere to force-reload the view
  // pages. Really this is just a nice-to-have that removes items directly deleted from My Music a
  // bit quicker than a call to the backend api to refresh the currently visible page
  let filtersState = state.get(CollectionKeys.SORTED_FILTERS);
  const allItems = state.getIn([CollectionKeys.COLLECTION, CollectionKeys.ITEMS]);
  const allAlbumsTracks = allItems.groupBy((item: $TSFixMe) => item.albumPandoraId);

  // Create special diffs for the songs view and album-based views since they display song/album
  // data differently
  const albumsRemovedIds = generateAlbumsRemovedIds(removed, allAlbumsTracks);
  const songsRemovedIds = generateSongsRemovedIds(removed);

  forEach(MUTABLE_FILTERS, (pandoraType, filter) => {
    let items = filtersState.getIn([filter, CollectionKeys.ITEMS]);
    const removedIds = pandoraType === D.PandoraTypes.SONG ? songsRemovedIds : albumsRemovedIds;
    items = items.filter((item: $TSFixMe) => !(item && includes(removedIds, item.pandoraId)));
    filtersState = filtersState.setIn([filter, CollectionKeys.ITEMS], items);
  });

  return state.set(CollectionKeys.SORTED_FILTERS, filtersState);
}

function onGetItems(state: $TSFixMe, action: $TSFixMe): $TSFixMe {
  D.Utils.Time.markTimeAtMoment(D.Constants.App.ApiCheckpointMarks.COLLECTIONS_GET_ITEMS_RESPONSE);
  const { version, cursor } = action.response;
  const addedItems = get(action, 'response.items', []);
  let itemsState;
  // Check if this is our initial load of collection ids
  if (!action.params.content.request.sinceVersion) {
    itemsState = action.params.content.request.cursor
      ? state.getIn([CollectionKeys.COLLECTION, CollectionKeys.ITEMS])
      : List();
    itemsState = itemsState.concat(List(addedItems));
    // Otherwise we're updating our collection ids
  } else {
    // Add new items, and make sure no duplicates are being added
    itemsState = state.getIn([CollectionKeys.COLLECTION, CollectionKeys.ITEMS]);
    const addedItemIds = map(addedItems, 'pandoraId');
    // Remove existing items with the same pandoraId as any of the newly added items
    itemsState = itemsState.filter((item: $TSFixMe) => {
      return !includes(addedItemIds, item.pandoraId);
    });
    itemsState = itemsState.concat(List(addedItems));

    // Remove items if necessary
    const removedItems = get(action, 'response.removedItems', []);
    const removedItemIds = map(removedItems, 'pandoraId');
    itemsState = itemsState.filter(
      (item: $TSFixMe) => removedItemIds.indexOf(item.pandoraId) === -1,
    );
  }
  return updateCollection(state, itemsState, version, cursor);
}

function onUpdateItems(state: $TSFixMe, action: $TSFixMe): $TSFixMe {
  if (!isPlaylistAction(action) && !hasCollectionUpdateSucceeded(action)) {
    return state;
  }

  const { added, removed, version, cursor } = action.response;

  let itemsState = state.getIn([CollectionKeys.COLLECTION, CollectionKeys.ITEMS], List());
  // Add items
  const addedItems = added || [];
  itemsState = itemsState.concat(addedItems);

  // Remove items
  const removedItems = removed || [];
  const removedItemIds = map(removedItems, 'pandoraId');
  itemsState = itemsState.filter((item: $TSFixMe) => removedItemIds.indexOf(item.pandoraId) === -1);

  let newState = updateCollection(state, itemsState, version, cursor);
  newState = updateFilters(newState, added, removed);
  return newState.set(CollectionKeys.SHOULD_RELOAD_ARTIST_VIEW, true);
}

function onCreatePlaylist(state: $TSFixMe, action: $TSFixMe): $TSFixMe {
  // Create a mostly correct playlist collection item to add to the relevant views until the
  // next they're refreshed. The only thing that's approximate is the addedTime
  let newState = state;
  const pandoraId = get(action, 'response.tempPandoraId');
  if (pandoraId) {
    const added = [
      {
        pandoraId,
        pandoraType: D.PandoraTypes.PLAYLIST,
        addedTime: D.getNow(),
      },
    ];
    const removed: $TSFixMe = [];
    // @ts-expect-error TS(2345) FIXME: Argument of type '{ pandoraId: any; pandoraType: "... Remove this comment to see the full error message
    newState = updateFilters(newState, added, removed);
  }
  return newState;
}

function resetFetchedDataSuccess(state: $TSFixMe): $TSFixMe {
  return state.set(CollectionKeys.FETCHED_DATA_SUCCESS_KEY, false);
}

function onGetSortedItemsFail(state: $TSFixMe, action: $TSFixMe): $TSFixMe {
  // typePrefixes is a property of the v6/collections/getSortedByTypes request body
  const typePrefixes = get(action, 'error.action.content.request.typePrefixes');
  let filterType;
  // Determine the current filter by the `typePrefixes` property in the request body
  // @ts-expect-error TS(2769) FIXME: No overload matches this call.
  if (isEqual(typePrefixes, AllTypes.concat(PodcastTypes)) || isEqual(typePrefixes, AllTypes)) {
    filterType = FilterTypes.ALL;
  } else if (isEqual(typePrefixes, PodcastTypes)) {
    filterType = FilterTypes.PODCASTS;
  } else {
    // @ts-expect-error TS(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    filterType = PandoraTypeToFilterTypeMap[typePrefixes[0]];
  }

  let newState = state;
  try {
    const data = JSON.parse(action.error.config.data);
    const offset = data.request.offset;
    const isRecentModifiedPlaylists = data.isRecentModifiedPlaylists;
    let filter = filterType;
    if (isRecentModifiedPlaylists) {
      filter = CollectionSorts.RECENT_MODIFIED_PLAYLISTS;
    }
    if (offset >= 0) {
      const fetchingOffsets = newState
        .getIn([CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.FETCHING_OFFSETS])
        .remove(offset);
      return newState.setIn(
        [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.FETCHING_OFFSETS],
        fetchingOffsets,
      );
    }
    return newState.setIn(
      [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.FETCHING_OFFSETS],
      Set(),
    );
  } catch (err) {
    if (filterType === FilterTypes.PLAYLISTS) {
      newState = newState.setIn(
        [
          CollectionKeys.SORTED_FILTERS,
          CollectionSorts.RECENT_MODIFIED_PLAYLISTS,
          CollectionKeys.FETCHING_OFFSETS,
        ],
        Set(),
      );
    }
    return newState.setIn(
      [CollectionKeys.SORTED_FILTERS, filterType, CollectionKeys.FETCHING_OFFSETS],
      Set(),
    );
  }
}

function getSortedItemsSuccessNewState(
  state: $TSFixMe,
  action: $TSFixMe,
  filter: $TSFixMe,
): $TSFixMe {
  const filterState = state.getIn([CollectionKeys.SORTED_FILTERS, filter]);
  const newIds = List(action.response.items);
  const previousOffset = action.response.offset;
  const fetchedOffsets = filterState.get(CollectionKeys.FETCHED_OFFSETS).add(previousOffset);
  const fetchingOffsets = filterState.get(CollectionKeys.FETCHING_OFFSETS).remove(previousOffset);
  let itemsState = filterState.get(CollectionKeys.ITEMS);
  const totalCount = filterState.get(CollectionKeys.TOTAL_COUNT) || 0;
  const countDiff = action.response.totalCount - totalCount;
  if (countDiff > 0) {
    for (let i = totalCount; i < action.response.totalCount; i++) {
      itemsState = itemsState.push(null);
    }
  } else if (countDiff < 0) {
    itemsState = itemsState.slice(0, action.response.totalCount);
  }

  let z = previousOffset;
  newIds.forEach(id => {
    itemsState = itemsState.set(z, id);
    z = z + 1;
  });

  let currentOffset = action.response.offset;
  currentOffset = currentOffset + action.response.limit;

  // If we've reached the end of the collection
  if (currentOffset > action.response.totalCount) {
    currentOffset = action.response.totalCount;
  }

  return state.withMutations((substate: $TSFixMe) => {
    substate
      .set(CollectionKeys.FETCHED_DATA_SUCCESS_KEY, true)
      .setIn([CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.ITEMS], itemsState)
      .setIn([CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.SHOULD_RELOAD], false)
      .setIn([CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.CURRENT_OFFSET], currentOffset)
      .setIn(
        [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.FETCHED_OFFSETS],
        fetchedOffsets,
      )
      .setIn(
        [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.FETCHING_OFFSETS],
        fetchingOffsets,
      )
      .setIn(
        [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.PREVIOUS_OFFSET],
        previousOffset,
      )
      .setIn(
        [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.TOTAL_COUNT],
        action.response.totalCount,
      )
      .setIn(
        [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.SORT],
        action.response.sortOrder,
      );
  });
}

// Given an action from getSortedItemsV5Success, return the filter we can also update
// from the same api response. Currently only used for most recent modified playlists.
function getFilterToAlsoUpdate(state: $TSFixMe, action: $TSFixMe, filter: $TSFixMe): $TSFixMe {
  // If we're getting fresh data for most recent modified playlists,
  // let's take advantage of this api response to update both my music and add to playlist submenu
  const currentFilter = state.get(CollectionKeys.CURRENT_FILTER);
  const currentSort = state.getIn([
    CollectionKeys.SORTED_FILTERS,
    currentFilter,
    CollectionKeys.SORT,
  ]);
  const sortOrderFromResponse = action.response.sortOrder;
  if (
    (filter === CollectionSorts.RECENT_MODIFIED_PLAYLISTS &&
      currentFilter === FilterTypes.PLAYLISTS &&
      currentSort === D.MyMusicSorts.RECENT) ||
    (filter === FilterTypes.PLAYLISTS && sortOrderFromResponse === CollectionSorts.RECENT_MODIFIED)
  ) {
    const filterToAlsoUpdate =
      filter === CollectionSorts.RECENT_MODIFIED_PLAYLISTS
        ? FilterTypes.PLAYLISTS
        : CollectionSorts.RECENT_MODIFIED_PLAYLISTS;
    return filterToAlsoUpdate;
  }
  return null;
}

function onGetSortedItemsSuccess(state: $TSFixMe, action: $TSFixMe): $TSFixMe {
  if (action.response.errorType) {
    log.error(action.type, action.response.message);
    return resetFetchedDataSuccess(state);
  }

  const typePrefixes = get(action, 'params.content.request.typePrefixes');
  let filter;

  if (isUndefined(typePrefixes)) {
    filter = getFilterFromView(action.response.view) || FilterTypes.ALL;
  } else {
    filter = getFilterFromTypePrefixes(typePrefixes);
  }

  if (filter === FilterTypes.PLAYLISTS) {
    D.Utils.Time.markTimeAtMoment(
      D.Constants.App.ApiCheckpointMarks.COLLECTIONS_GET_SORTED_PLAYLISTS_RESPONSE,
    );
    const isRecentModifiedPlaylists = action.params.content.isRecentModifiedPlaylists;
    // Save data in RECENT_MODIFIED_PLAYLISTS because getSorted call was initiated by add to playlist submenu
    if (isRecentModifiedPlaylists) {
      filter = CollectionSorts.RECENT_MODIFIED_PLAYLISTS;
    }
  }

  let newState = state;
  newState = getSortedItemsSuccessNewState(newState, action, filter);
  const filterToAlsoUpdate = getFilterToAlsoUpdate(newState, action, filter);
  if (filterToAlsoUpdate) {
    newState = getSortedItemsSuccessNewState(newState, action, filterToAlsoUpdate);
  }
  return newState;
}

function setFetchingOffsets(state: $TSFixMe, { payload: { filter, offset } }: $TSFixMe): $TSFixMe {
  let filterState = state.getIn([CollectionKeys.SORTED_FILTERS, filter]);
  const fetchingOffsets = filterState.get(CollectionKeys.FETCHING_OFFSETS).add(offset);
  filterState = filterState.set(CollectionKeys.FETCHING_OFFSETS, fetchingOffsets);

  return state.mergeDeep({
    [CollectionKeys.SORTED_FILTERS]: {
      [filter]: filterState,
    },
  });
}

function resetFilterOffsets(state: $TSFixMe, { payload: { filter } }: $TSFixMe): $TSFixMe {
  return state.setIn(
    [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.FETCHED_OFFSETS],
    Set(),
  );
}

function setFilterCurrentViewedOffset(
  state: $TSFixMe,
  { payload: { filter, offset } }: $TSFixMe,
): $TSFixMe {
  return state.setIn(
    [CollectionKeys.SORTED_FILTERS, filter, CollectionKeys.CURRENT_VIEWED_OFFSET],
    offset,
  );
}

function setCurrentNavFilter(state: $TSFixMe, { payload }: $TSFixMe): $TSFixMe {
  return state.set(CollectionKeys.CURRENT_FILTER, payload.filter);
}

function onGetItemsByArtistSuccess(state: $TSFixMe, action: $TSFixMe): $TSFixMe {
  const artistId = action.params.content.request.artistPandoraId;
  let artistsList = state.get(CollectionKeys.ARTISTS_LIST);
  if (artistsList.includes(artistId)) {
    return state.mergeDeep({
      [CollectionKeys.SHOULD_RELOAD_ARTIST_VIEW]: false,
      [CollectionKeys.ARTISTS_ITEMS_ID_LIST]: action.response.items,
    });
  }
  artistsList = state.get(CollectionKeys.ARTISTS_LIST).push(artistId);
  return state.mergeDeep({
    [CollectionKeys.ARTISTS_LIST]: artistsList,
    [CollectionKeys.SHOULD_RELOAD_ARTIST_VIEW]: false,
    [CollectionKeys.ARTISTS_ITEMS_ID_LIST]: action.response.items,
  });
}

function setCurrentFilterSort(state: $TSFixMe, { payload }: $TSFixMe): $TSFixMe {
  const currentFilter = state.get(CollectionKeys.CURRENT_FILTER);
  const currentTotalCount = state.getIn(
    [CollectionKeys.SORTED_FILTERS, currentFilter, CollectionKeys.TOTAL_COUNT],
    null,
  );
  return state.setIn(
    [CollectionKeys.SORTED_FILTERS, currentFilter],
    Map({
      [CollectionKeys.ITEMS]: List(),
      [CollectionKeys.FETCHED_OFFSETS]: Set(),
      [CollectionKeys.FETCHING_OFFSETS]: Set(),
      [CollectionKeys.CURRENT_OFFSET]: null,
      [CollectionKeys.PREVIOUS_OFFSET]: null,
      [CollectionKeys.SHOULD_RELOAD]: true,
      [CollectionKeys.SORT]: payload.sort,
      [CollectionKeys.TOTAL_COUNT]: null,
      [CollectionKeys.PREVIOUS_TOTAL_COUNT]: currentTotalCount,
    }),
  );
}

function addRecentlyPlayedPodcasts(state: $TSFixMe, action: $TSFixMe): $TSFixMe {
  const { payload } = action;
  const listenerId = action.sharedState.listenerId;

  if (!listenerId) {
    return state;
  }

  const currentPodcasts = state
    .getIn([CollectionKeys.RECENTLY_PLAYED_PODCASTS, listenerId], List())
    .filter((current: $TSFixMe) => {
      return current && current.id !== payload.id;
    });

  const recentPodcasts = [payload, ...currentPodcasts].slice(
    0,
    RECENTLY_PLAYED_PODCASTS_MAX_LIST_SIZE,
  );

  return state.mergeIn([CollectionKeys.RECENTLY_PLAYED_PODCASTS], {
    [listenerId]: List(recentPodcasts),
  });
}

function setRecentlyPlayedSPSItems(state: $TSFixMe, action: $TSFixMe): $TSFixMe {
  const { payload } = action;
  const listenerId = action.sharedState.listenerId;

  if (!listenerId) {
    return state;
  }

  return state.mergeIn([CollectionKeys.RECENTLY_PLAYED_SPS_ITEMS], {
    [listenerId]: payload,
  });
}

function setHasMetRecentlyPlayedThreshold(state: $TSFixMe, { payload }: $TSFixMe): $TSFixMe {
  return state.set(
    CollectionKeys.HAS_MET_RECENTLY_PLAYED_THRESHOLD,
    payload.hasMetRecentlyPlayedThreshold,
  );
}
